import React, { useCallback } from 'react'

import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { SOURCE_TYPES } from 'constants/source'
import type OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import type useGuestCheckoutEligibility from 'hooks/useGuestCheckoutEligibility'
import type useMetasearchAdvertisedPrice from 'hooks/useMetasearchAdvertisedPrice'

import PropertyPayAtPropertyModal from './PropertyPayAtPropertyModal'
import { propertySingleRoomBookNowOrAddToCartCallback } from './propertyBuyCallback'
import type { getPayAtPropertyDetails } from './singlePropertyUtils'
import type useInvalidateStalePrefetchedFlightsFromOffer from '../hooks/prefetchFlights/useInvalidateStalePrefetchedFlightsFromOffer'

type PurchaseArgs = Parameters<typeof propertySingleRoomBookNowOrAddToCartCallback>[0]

interface Params extends Pick<PurchaseArgs,
  'airports' |
  'currentCurrency' |
  'dispatch' |
  'duration' |
  'intl' |
  'isLoggedIn' |
  'isAccommodationV2BookingsEnabled' |
  'isSpoofed' |
  'memberId' |
  'modalElementContext' |
  'metasearchPriceId' |
  'metasearchRuleId' |
  'offerPageDispatch' |
  'property' |
  'rate' |
  'rateListId' |
  'ratePosition' |
  'regionCode' |
  'roomListId' |
  'roomOption' |
  'roomPosition' |
  'rooms' |
  'showModal' |
  'source' |
  'subscriber' |
  'utmContent' |
  'voucherSource' |
  'ghaPriceMatch'> {
  activateGuestCheckoutExperiment: ReturnType<typeof useGuestCheckoutEligibility>['activate']
  canProceedAsGuest: ReturnType<typeof useGuestCheckoutEligibility>['canProceedAsGuest']
  checkIn: string | undefined
  checkOut: string | undefined
  invalidateStalePrefetchedFlightsFromOffer: ReturnType<typeof useInvalidateStalePrefetchedFlightsFromOffer>
  metasearchAdvertised: ReturnType<typeof useMetasearchAdvertisedPrice>
  accountType: PurchaseArgs['metasearchAccountType']
  originAirport: App.Airport | undefined
  payAtPropertyDetails: ReturnType<typeof getPayAtPropertyDetails>
  isMetasearchDiscountEligibleRate: boolean | undefined
  discountAmount: number
  guestCheckoutPurchase: Parameters<ReturnType<typeof useGuestCheckoutEligibility>['canProceedAsGuest']>[0]
  experiencesPassportCheckoutSources: NonNullable<PurchaseArgs['experiencesPassportCheckoutSources']>
  trackOfferPageEvent: React.ContextType<typeof OfferPageStateTrackingContext>['trackEvent']
  loyaltyPackageUpgrade: PurchaseArgs['loyalty']
  selectedOptionalBundleIds: Array<string>
  selectedOptionalBundlesTotalPrice: number
  canViewLuxPlusBenefits: boolean
  displayedMemberPrice: number | undefined
  displayedNonMemberPrice: number
  displayedValue: number
}

function usePropertyRatePurchase(params: Params) {
  const {
    airports,
    activateGuestCheckoutExperiment,
    canProceedAsGuest,
    checkIn,
    checkOut,
    currentCurrency,
    dispatch,
    duration,
    invalidateStalePrefetchedFlightsFromOffer,
    intl,
    isLoggedIn,
    isAccommodationV2BookingsEnabled,
    isSpoofed,
    memberId,
    modalElementContext,
    metasearchAdvertised,
    metasearchPriceId,
    accountType,
    metasearchRuleId,
    offerPageDispatch,
    originAirport,
    payAtPropertyDetails,
    property,
    rate,
    rateListId,
    ratePosition,
    regionCode,
    roomListId,
    roomOption,
    roomPosition,
    rooms,
    isMetasearchDiscountEligibleRate,
    discountAmount,
    guestCheckoutPurchase,
    experiencesPassportCheckoutSources,
    showModal,
    source,
    subscriber,
    trackOfferPageEvent,
    utmContent,
    voucherSource,
    ghaPriceMatch,
    loyaltyPackageUpgrade,
    selectedOptionalBundleIds,
    selectedOptionalBundlesTotalPrice,
    canViewLuxPlusBenefits,
    displayedMemberPrice,
    displayedNonMemberPrice,
    displayedValue,
  } = params

  const onCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren’t set')
    if (!shouldAddToCart) {
      invalidateStalePrefetchedFlightsFromOffer()
    }
    trackOfferPageEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    // After a successful login modal the Redux selector can still be stale in this closure.
    let bookedWhileLoggedIn = isLoggedIn
    const isGuestCheckout = !shouldAddToCart && canProceedAsGuest(guestCheckoutPurchase)
    if (!bookedWhileLoggedIn) {
      activateGuestCheckoutExperiment(guestCheckoutPurchase)
      if (!isGuestCheckout) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result?.loggedIn) {
          return
        }
        bookedWhileLoggedIn = true
      }
    }

    const chosenRate = (payAtPropertyDetails.shouldShowPill && payAtPropertyDetails.shouldShowModal)
      ? await showModal<App.EnrichedPropertyRate | undefined>(
          <PropertyPayAtPropertyModal
            propertyTimeZone={property.location.timezone}
            duration={duration}
            propertyCollectRate={payAtPropertyDetails.propertyCollectRate}
            leCollectRate={payAtPropertyDetails.leCollectRate}
            optInBundlesPrice={selectedOptionalBundlesTotalPrice}
          />,
        )
      : rate
    if (!chosenRate) return
    // The upgrade payload only belongs to this option's own rate (not e.g. a pay-at-property variant).
    const checkoutLoyaltyPackageUpgrade = chosenRate === rate ? loyaltyPackageUpgrade : undefined
    // Applied ⇒ book the target rate (charging the source); available-only ⇒ book the source as normal.
    const checkoutRate = checkoutLoyaltyPackageUpgrade?.upgrade.applied
      ? checkoutLoyaltyPackageUpgrade.targetRate
      : chosenRate

    propertySingleRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      currentCurrency,
      airports,
      currentRegionCode: regionCode,
      memberId,
      offerPageDispatch,
      modalElementContext,
      showModal,
      intl,
      property,
      rate: checkoutRate,
      ratePosition,
      originAirportCode: originAirport?.code,
      checkIn,
      checkOut,
      duration,
      rooms,
      regionCode,
      source: source ? `${source},${SOURCE_TYPES.CTA}` : SOURCE_TYPES.CTA,
      utmContent,
      roomOption,
      isSpoofed,
      isLoggedIn: bookedWhileLoggedIn,
      isAccommodationV2BookingsEnabled,
      isGuestCheckout,
      voucherSource,
      ghaPriceMatch,
      subscriber,
      roomListId,
      roomPosition,
      rateListId,
      loyalty: checkoutLoyaltyPackageUpgrade,
      metasearchPriceId,
      metasearchAccountType: accountType,
      metasearchRuleId,
      metasearchAdvertisedPrice: metasearchAdvertised?.advertisedPrice,
      metasearchAdvertisedCurrency: metasearchAdvertised?.advertisedCurrency,
      shouldApplyDiscount: isMetasearchDiscountEligibleRate,
      ghaDiscountAmount: discountAmount,
      experiencesPassportCheckoutSources,
      selectedOptionalBundleIds: chosenRate === rate && selectedOptionalBundleIds.length ? selectedOptionalBundleIds : undefined,
      isLuxPlusMember: canViewLuxPlusBenefits,
      displayMemberPrice: displayedMemberPrice ?? displayedNonMemberPrice,
      displayNonMemberPrice: displayedNonMemberPrice,
      displayValue: displayedValue,
    })
  }, [
    airports,
    activateGuestCheckoutExperiment,
    canProceedAsGuest,
    checkIn,
    checkOut,
    currentCurrency,
    dispatch,
    duration,
    invalidateStalePrefetchedFlightsFromOffer,
    intl,
    isLoggedIn,
    isAccommodationV2BookingsEnabled,
    isSpoofed,
    memberId,
    modalElementContext,
    metasearchAdvertised,
    metasearchPriceId,
    accountType,
    metasearchRuleId,
    offerPageDispatch,
    originAirport?.code,
    payAtPropertyDetails,
    property,
    rate,
    rateListId,
    ratePosition,
    regionCode,
    roomListId,
    roomOption,
    roomPosition,
    rooms,
    isMetasearchDiscountEligibleRate,
    discountAmount,
    guestCheckoutPurchase,
    experiencesPassportCheckoutSources,
    showModal,
    source,
    subscriber,
    trackOfferPageEvent,
    utmContent,
    voucherSource,
    ghaPriceMatch,
    loyaltyPackageUpgrade,
    selectedOptionalBundleIds,
    selectedOptionalBundlesTotalPrice,
    canViewLuxPlusBenefits,
    displayedMemberPrice,
    displayedNonMemberPrice,
    displayedValue,
  ])

  const handleBookNowFromModal = useCallback(() => {
    onCtaClick(false)
  }, [onCtaClick])

  const handleAddToCartFromModal = useCallback(() => {
    onCtaClick(true)
  }, [onCtaClick])

  return { onCtaClick, handleBookNowFromModal, handleAddToCartFromModal }
}

export default usePropertyRatePurchase
